// Pure helpers for the community-services domains (dining/menu,
// activities/schedule, housekeeping). No JSX, no server calls - the staff
// pages, the resident Today panel and Aria's tool executor all read the
// same backend records (routes/menu.py, routes/schedule.py, StaffTask) and
// use these to present them consistently.

// Departments whose /workspace is the community-services view
// (CommunityServicesWorkspace.jsx). Maintenance and transportation keep
// their own views in DepartmentWorkspace.jsx.
export const SERVICES_DEPARTMENTS = ["housekeeping", "kitchen", "activities"];

export const MEAL_PERIODS = ["breakfast", "lunch", "dinner"];

// Menu item status -> what staff see. Only "approved" is ever read by
// residents or Aria (routes/menu.py::public_today). "superseded" items were
// replaced by a later approved upload or a published correction and must not
// be re-approved from the list - they stay for history only.
const MENU_STATUS = {
  approved: { label: "Published", tone: "live", canApprove: false },
  draft: { label: "Draft", tone: "draft", canApprove: true },
  superseded: { label: "Replaced", tone: "old", canApprove: false },
};

export function menuStatusView(status) {
  return MENU_STATUS[status] || MENU_STATUS.draft;
}

// Schedule item status. Rows written before the draft/published split
// have no status and were always live, so a missing status is "published".
const SCHEDULE_STATUS = {
  published: { label: "Published", tone: "live", canPublish: false },
  draft: { label: "Draft", tone: "draft", canPublish: true },
  superseded: { label: "Replaced", tone: "old", canPublish: false },
};

export function scheduleStatusView(status) {
  return SCHEDULE_STATUS[status || "published"] || SCHEDULE_STATUS.published;
}

// Draft schedule rows grouped by the ingest batch (one pasted or emailed
// calendar) that produced them, newest batch first, rows by date.
export function groupDraftsByBatch(items) {
  const groups = new Map();
  for (const it of items || []) {
    const key = it.ingest_id || `single:${it.schedule_id}`;
    if (!groups.has(key)) groups.set(key, { ingest_id: it.ingest_id || null, source: it.source, created_at: it.created_at, items: [] });
    groups.get(key).items.push(it);
  }
  const out = [...groups.values()];
  for (const g of out) g.items.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  return out.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
}

function dayWords(date) {
  return date ? date : "today";
}

// Dietary/allergen labels are staff-entered on the menu item and repeated
// verbatim (routes/menu.py::public_today). Nothing is inferred from them: a
// missing allergen is not a claim the dish is free of it.
export const MENU_LABELS_NOTE =
  "Dietary and allergen labels are entered by kitchen staff. If an allergen is not listed, that does not mean " +
  "the dish is free of it - please check with the kitchen staff.";

export function menuLabelsText(item) {
  const diet = (item?.dietary_labels || []).join(", ");
  const allergens = (item?.allergens || []).join(", ");
  return [diet, allergens && `contains: ${allergens}`].filter(Boolean).join("; ");
}

// Staff type labels as a comma-separated list; the backend normalizes and bounds them.
export function parseMenuLabels(text) {
  return (text || "").split(",").map((s) => s.trim()).filter(Boolean);
}

// Aria's get_menu result text. Items come from /menu/public/today, which
// only ever returns approved items. An empty answer must stay honest: no
// promise to "check and get back", because nothing has been sent anywhere.
export function menuToolMessage(items, { mealPeriod = null, date = null } = {}) {
  const what = mealPeriod || "menu";
  if (!items || !items.length) {
    // Room 214 rt_d3ahe1bf 2026-10-21: the rules below were read aloud ("I can only promise that it was
    // requested if the tool says it was submitted"). The spoken fact and the instructions are now separate.
    return `No approved ${what} is on file for ${dayWords(date)}. ` +
      `Say only that, in your own words (e.g. "I don't have tonight's ${what} yet."). ` +
      "Instructions for you, never to be said aloud: nothing has been requested from the kitchen; if they " +
      "want, you may offer once to ask the kitchen (request_staff_help, category kitchen) and say it was asked " +
      "only after that succeeds.";
  }
  const lines = items.map((i) => {
    const labels = menuLabelsText(i);
    return `${i.meal_period}: ${i.item_name}${i.description ? ` - ${i.description}` : ""}` +
      `${i.availability ? ` (${i.availability})` : ""}${labels ? ` [${labels}]` : ""}`;
  });
  const labelRule = items.some((i) => menuLabelsText(i))
    ? " Labels in [brackets] are entered by kitchen staff: repeat them exactly. Do not say a dish is safe or suitable " +
      "for anyone, and an allergen that is not listed is not a statement that it is absent - suggest checking with the kitchen staff."
    : "";
  return `Approved menu for ${dayWords(date)}: ${lines.join("; ")}.${labelRule}`;
}

// Aria's get_todays_schedule result text (the endpoint already returns
// only published, resident-facing rows in time order).
export function scheduleToolMessage(items, { date = null } = {}) {
  if (!items || !items.length) {
    return `Nothing is listed on the schedule for ${dayWords(date)} yet. Say so honestly - do not invent an activity or time.`;
  }
  const lines = items.map((i) =>
    `${i.time_label ? `${i.time_label}: ` : ""}${i.title}${i.description ? ` (${i.description})` : ""}`);
  return `Schedule for ${dayWords(date)}: ${lines.join("; ")}.`;
}

// The resident's own planned appointments/jobs on that day (GET
// /tasks/resident-request/upcoming items), added to the schedule answer so
// "when is my therapy?" is answered from their real plan, not only the
// activities calendar.
export function ownPlanLine(upcoming, date) {
  const day = date || upcoming?.today;
  const mine = (upcoming?.items || []).filter((i) => (i.scheduled_date || i.requested_for_date) === day);
  if (!mine.length) return "";
  const parts = mine.map((i) => {
    const t = i.scheduled_time_label || i.requested_for_time_label;
    const with_ = [i.provider_name && `with ${i.provider_name}`, i.location && `at ${i.location}`].filter(Boolean).join(", ");
    return `${t ? `${t}: ` : ""}${i.what_for || i.title || "an appointment"}${with_ ? ` (${with_})` : ""}`;
  });
  return ` The resident's own planned appointments/jobs that day: ${parts.join("; ")}.`;
}
